import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { fmtDay, initials } from "@/lib/utils";

interface Celebration {
  kind: "birthday" | "anniversary";
  id: string;
  display_name: string;
  last_name: string | null;
  profile_picture_path: string | null;
  department: string | null;
  occurs_on: string;
  years: number | null;
}

// Celebrations change at most once a day. Cache in localStorage and only
// refetch if more than 6 hours have elapsed since the last fetch.
const CACHE_KEY = "scenta-hq-celebrations-v1";
const CACHE_MAX_AGE_MS = 6 * 60 * 60 * 1000;

export function CelebrationsBanner() {
  const [items, setItems] = useState<Celebration[]>(() => {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw) as { fetched_at: number; items: Celebration[] };
      return Array.isArray(parsed.items) ? parsed.items : [];
    } catch { return []; }
  });
  useEffect(() => {
    // Skip the fetch entirely if our cache is still fresh.
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as { fetched_at: number };
        if (Date.now() - parsed.fetched_at < CACHE_MAX_AGE_MS) return;
      }
    } catch { /* fall through to fetch */ }
    void supabase.from("hq_upcoming_celebrations").select("*").limit(10).then(({ data }) => {
      const list = (data as Celebration[]) ?? [];
      setItems(list);
      try { localStorage.setItem(CACHE_KEY, JSON.stringify({ fetched_at: Date.now(), items: list })); } catch { /* ignore */ }
    });
  }, []);

  if (items.length === 0) return null;
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="rounded-xl bg-gradient-to-r from-teal-mint via-white to-sand p-5 ring-1 ring-line">
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-teal-dark">🎂 Upcoming celebrations</h2>
      <div className="flex flex-wrap gap-3">
        {items.map(c => {
          const isToday = c.occurs_on.slice(0, 10) === today;
          const label = c.kind === "birthday"
            ? `${c.display_name}'s birthday`
            : `${c.display_name}'s ${c.years}-year anniversary`;
          return (
            <div key={`${c.kind}-${c.id}-${c.occurs_on}`} className="flex items-center gap-2 rounded-full bg-white/80 px-3 py-1.5 ring-1 ring-line">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-teal-mint text-xs font-bold text-teal-dark">
                {initials(c.display_name, c.last_name, "")}
              </span>
              <span className="text-sm">
                {c.kind === "birthday" ? "🎂" : "🎉"} <strong>{label}</strong>
                <span className="text-gray2"> · {isToday ? "Today!" : fmtDay(c.occurs_on)}</span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
